import {
  Attachment,
  Link as LinkIcon,
  LinkOff,
  OpenInNew,
  Public,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Switch,
  Typography,
} from '@mui/material';
import { RawDraftContentState } from 'draft-js';
import { Dispatch, useState } from 'react';
import ReactGA from 'react-ga4';
import { extractAllLinksFromBody } from './extractLinksFromBody';
import { removeInlineLinkFromBody } from './removeLinkFromBody';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useLazyQuery } from '@apollo/client';
import {
  GetBodyWithSuggestedLinksQuery,
  GetBodyWithSuggestedLinksQueryVariables,
  BrandKey,
  SuggestInternalLinksQuery,
  SuggestInternalLinksQueryVariables,
} from '../../../../__generated__/queries-web';
import {
  NotificationTypeEnum,
  useDialog,
  useNotification,
} from '@prismamedia/one-components';
import {
  GET_BODY_WITH_SUGGESTED_LINKS,
  SUGGEST_INTERNAL_LINKS,
} from '../../../../apollo/queries/suggestLinks.web.graphql';
import { actions, ArticleEditAction } from '../../reducer';

const SuggestLinksDialog = ({
  onClose,
  body,
  dispatch,
  suggestedLinks,
  idArticle,
}: {
  onClose: () => void;
  body?: string;
  dispatch: Dispatch<ArticleEditAction>;
  suggestedLinks: SuggestInternalLinksQuery['suggestInternalLinks'];
  idArticle: string;
}) => {
  const { pushNotification } = useNotification();
  const [selectedLinks, setSelectedLinks] = useState<string[]>([]);

  // Helper function to highlight the word in the sentence
  const highlightWordInSentence = (sentence: string, word: string) => {
    const regex = new RegExp(`(${word})`, 'gi');
    const parts = sentence.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === word.toLowerCase()) {
        return <strong key={index}>{part}</strong>;
      }
      return part;
    });
  };

  const handleLinkToggle = (linkUrl: string) => {
    setSelectedLinks((prev) => {
      if (prev.includes(linkUrl)) {
        return prev.filter((url) => url !== linkUrl);
      }
      return [...prev, linkUrl];
    });
  };

  const [
    addSuggestedLinks,
    { loading: loadingAddSuggestedLinks },
  ] = useLazyQuery<
    GetBodyWithSuggestedLinksQuery,
    GetBodyWithSuggestedLinksQueryVariables
  >(GET_BODY_WITH_SUGGESTED_LINKS, {
    fetchPolicy: 'network-only',
    onError: () => {
      pushNotification({
        message: "Une erreur est survenue lors de l'ajout des liens",
        type: NotificationTypeEnum.error,
      });
    },
  });

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Suggestion de liens internes
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ textAlign: 'center' }}>
          {suggestedLinks.length > 0 ? (
            suggestedLinks.map((link, index) => {
              return (
                <Box key={link.url} sx={{ mx: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ fontWeight: 'bold' }}>{link.word}</Box>

                    <Box sx={{ fontStyle: 'italic' }}>
                      {highlightWordInSentence(link.sentence, link.word)}
                    </Box>
                    <Link
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      underline="hover"
                      sx={{ color: 'black', textDecoration: 'underline' }}
                    >
                      {link.title}
                    </Link>
                    <Box>{link.relevance}</Box>
                    <FormControlLabel
                      sx={{
                        justifyContent: 'center',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px',
                        },
                      }}
                      control={
                        <Switch
                          checked={selectedLinks.includes(link.url)}
                          onChange={() => handleLinkToggle(link.url)}
                          size="small"
                        />
                      }
                      label="Ajouter"
                    />
                  </Box>
                  {index < suggestedLinks.length - 1 && (
                    <Divider sx={{ mt: 2, mb: 2 }} />
                  )}
                </Box>
              );
            })
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography>Aucun lien trouvé</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {loadingAddSuggestedLinks ? (
          <CircularProgress size={16} />
        ) : (
          <Button
            variant="outlined"
            onClick={() => {
              if (body && selectedLinks.length > 0) {
                ReactGA.event({
                  category: 'Suggestion liens internes',
                  action: 'Suggestion liens — Ajouter',
                  label: idArticle,
                  value: selectedLinks.length,
                });
                // Filter suggestedLinks to only include selected ones
                const selectedSuggestedLinks = suggestedLinks.filter((link) =>
                  selectedLinks.includes(link.url),
                );

                addSuggestedLinks({
                  variables: {
                    body,
                    links: selectedSuggestedLinks.map((link) => ({
                      url: link.url,
                      word: link.word,
                      sentence: link.sentence,
                      blockKey: link.blockKey,
                    })),
                  },
                  onCompleted: (data) => {
                    dispatch(
                      actions.updateField({
                        field: 'body',
                        value: JSON.parse(
                          data.getBodyWithSuggestedLinks.updatedBody,
                        ),
                      }),
                    );
                    onClose();
                  },
                });
              }
            }}
            disabled={selectedLinks.length === 0}
          >
            Ajouter ({selectedLinks.length})
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>
          Fermer
        </Button>
      </DialogActions>
    </>
  );
};

export const LinksContent = ({
  body,
  title,
  brandKey,
  dispatch,
  idArticle,
}: {
  body: RawDraftContentState | null;
  title: string;
  brandKey: BrandKey;
  dispatch: Dispatch<ArticleEditAction>;
  idArticle: string;
}) => {
  const { pushNotification } = useNotification();

  const { openDialog, closeDialog } = useDialog();
  const links = extractAllLinksFromBody(body);

  const handleDeleteLink = (entityKey: string) => {
    if (!body) {
      return;
    }

    dispatch(
      actions.updateField({
        field: 'body',
        value: removeInlineLinkFromBody(body, entityKey),
      }),
    );
  };

  const [
    suggestInternalLinks,
    { loading: loadingSuggestInternalLinks },
  ] = useLazyQuery<
    SuggestInternalLinksQuery,
    SuggestInternalLinksQueryVariables
  >(SUGGEST_INTERNAL_LINKS, {
    fetchPolicy: 'network-only',
    onError: () => {
      pushNotification({
        message: 'Une erreur est survenue lors de la complétion du diaporama',
        type: NotificationTypeEnum.error,
      });
    },
    onCompleted: (data) => {
      openDialog(
        <SuggestLinksDialog
          body={JSON.stringify(body)}
          idArticle={idArticle}
          onClose={() => closeDialog()}
          dispatch={dispatch}
          suggestedLinks={data.suggestInternalLinks}
        />,
      );
    },
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 2,
          '& .icon': {
            display: 'flex',
            alignItems: 'center',
            '& p': {
              paddingLeft: '1rem',
              fontWeight: 500,
            },
          },
        }}
      >
        <div className="icon">
          <Attachment />
          <Typography>{`Liens de l'article (${links.length})`}</Typography>
        </div>
        <Box sx={{ marginLeft: '1rem', pt: '1.5px' }}>
          {loadingSuggestInternalLinks ? (
            <CircularProgress size={16} />
          ) : (
            <Box
              onClick={async () => {
                ReactGA.event({
                  category: 'Suggestion liens internes',
                  action: 'Suggestion liens — Bêta (génération)',
                  label: idArticle,
                });
                await suggestInternalLinks({
                  variables: {
                    body: JSON.stringify(body),
                    title,
                    brandKey: brandKey as BrandKey,
                    existingLinksLabels: links.map(({ label }) => label),
                    idArticle,
                  },
                });
              }}
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <AutoFixHighIcon
                sx={{
                  color: '#E91E63',
                  fontSize: '18px',
                }}
                aria-label="Bêta"
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: '#E91E63',
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                Bêta
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {(() => {
        const internalLinks = links.filter((l) => l.isInternal);
        const externalLinks = links.filter((l) => !l.isInternal);
        return (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  bgcolor: 'rgba(37, 99, 235, 0.1)',
                  py: 2,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: 'rgb(37, 99, 235)' }}
                >
                  {internalLinks.length}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgb(37, 99, 235)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  Internes
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  bgcolor: 'rgba(168, 85, 247, 0.1)',
                  py: 2,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: 'rgb(168, 85, 247)' }}
                >
                  {externalLinks.length}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgb(168, 85, 247)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  Externes
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}
            >
              <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Liens détectés ({links.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[...internalLinks, ...externalLinks].map((linkItem, index) => {
                const {
                  entityKey,
                  label,
                  link,
                  isInternal,
                  source,
                  type,
                } = linkItem;
                const isInlineLink = !!entityKey && !source;

                return (
                  <Box
                    key={entityKey ?? `${link}-${index}`}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1.5,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          flexShrink: 0,
                          bgcolor: isInternal
                            ? 'rgba(37, 99, 235, 0.12)'
                            : 'rgba(168, 85, 247, 0.12)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            color: isInternal
                              ? 'rgb(37, 99, 235)'
                              : 'rgb(168, 85, 247)',
                          }}
                        >
                          {isInternal ? 'Interne' : 'Externe'}
                        </Typography>
                      </Box>
                      {type === 'People' && (
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            flexShrink: 0,
                            bgcolor: 'rgba(233, 30, 99, 0.12)',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              color: 'rgb(233, 30, 99)',
                            }}
                          >
                            People
                          </Typography>
                        </Box>
                      )}
                      {source && (
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            flexShrink: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.06)',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              color: 'text.secondary',
                            }}
                          >
                            {source}
                          </Typography>
                        </Box>
                      )}
                      {type === 'People' ? (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            alignItems: 'baseline',
                            display: 'flex',
                            gap: 0.5,
                            minWidth: 0,
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color:
                                type === 'Doublon'
                                  ? 'success.main'
                                  : 'text.primary',
                              flexShrink: 0,
                              fontWeight: 600,
                            }}
                          >
                            {`${type}:`}
                          </Typography>
                          <Typography
                            component="a"
                            href={link}
                            rel="noreferrer"
                            target="_blank"
                            variant="body2"
                            sx={{
                              color:
                                type === 'Doublon'
                                  ? 'success.main'
                                  : 'text.primary',
                              minWidth: 0,
                              overflow: 'hidden',
                              textDecoration: 'underline',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Public sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {link}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        component="a"
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.25,
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <OpenInNew sx={{ fontSize: 14 }} />
                        Ouvrir
                      </Box>
                      {isInlineLink && (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleDeleteLink(entityKey)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            fontSize: '0.75rem',
                            color: 'error.main',
                            textDecoration: 'none',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            p: 0,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          <LinkOff sx={{ fontSize: 14 }} />
                          Supprimer
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        );
      })()}
    </Box>
  );
};
